import { useState, useEffect, useCallback } from 'react';
import { ViewMode } from '../types';
import { PERSISTENT_PROJECT_VIEWS } from './useViewNavigationPersistence';

interface UseAppViewTabsStateParams {
  activeWorkspace: 'professional' | 'private';
  setViewMode: (mode: ViewMode) => void;
  setActiveProjectTab: (tab: 'all' | 'active' | 'archived') => void;
  setCurrentProjectId: (id: string) => void;
  setFilterAssignee: (val: string) => void;
  setFilterType: (val: string) => void;
  setFilterStatus: (val: string) => void;
  setFilterPriority: (val: string) => void;
  setSearchQuery: (val: string) => void;
  uiMode: 'standard' | 'expert';
  setUiMode: (mode: 'standard' | 'expert') => void;
  setShowBiographyModal: (open: boolean) => void;
}

export function useAppViewTabsState({
  activeWorkspace,
  setViewMode,
  setActiveProjectTab,
  setCurrentProjectId,
  setFilterAssignee,
  setFilterType,
  setFilterStatus,
  setFilterPriority,
  setSearchQuery,
  uiMode,
  setUiMode,
  setShowBiographyModal,
}: UseAppViewTabsStateParams) {
  const [selectedMetricsProjectId, setSelectedMetricsProjectId] = useState<string>('all');
  const [activeAnalysisTab, setActiveAnalysisTab] = useState<'swot' | 'stakeholder' | 'risk' | 'toc' | 'raci' | 'participation'>('swot');
  const [selectedAnalysisProjectId, setSelectedAnalysisProjectId] = useState<string>('');
  const [activeMetricsTab, setActiveMetricsTab] = useState<'overview' | 'roadmap' | 'time' | 'projects' | 'library' | 'ideaPool' | 'annualReport' | 'activity'>('overview');
  const [activeCalendarTab, setActiveCalendarTab] = useState<'calendar' | 'milestones'>('calendar');
  const [calendarShowAllTasks, setCalendarShowAllTasks] = useState(true);
  const [aiAssistantTab, setAiAssistantTab] = useState<'inbox' | 'planning' | 'protocol'>('inbox');
  const [activePoolTab, setActivePoolTab] = useState<'activeProjects' | 'archive' | 'library' | 'ideaPool'>('activeProjects');
  const [hiddenProjectIds, setHiddenProjectIds] = useState<string[]>([]);
  const [isTimeMachineOpen, setIsTimeMachineOpen] = useState(false);
  const [calendarShowOtherWorkspace, setCalendarShowOtherWorkspace] = useState(false);

  const [isRightSidebarOpen, setIsRightSidebarOpen] = useState(() => {
    return localStorage.getItem('gwantt_right_sidebar_open') === 'true';
  });

  const toggleRightSidebar = useCallback(() => {
    setIsRightSidebarOpen(prev => {
      const next = !prev;
      localStorage.setItem('gwantt_right_sidebar_open', String(next));
      return next;
    });
  }, []);

  const [syncProjectViews, setSyncProjectViews] = useState<boolean>(() => {
    return localStorage.getItem('gwantt_sync_project_views') === 'true';
  });

  useEffect(() => {
    localStorage.setItem('gwantt_sync_project_views', String(syncProjectViews));
  }, [syncProjectViews]);

  useEffect(() => {
    setSelectedMetricsProjectId('all');
    setSelectedAnalysisProjectId('');
    setHiddenProjectIds([]);
  }, [activeWorkspace]);

  const setActivePoolTabWithTransition = useCallback((tab: 'activeProjects' | 'archive' | 'library' | 'ideaPool') => {
    setActivePoolTab(tab);
  }, []);

  const setViewModeWithTransition = useCallback((mode: ViewMode) => {
    setViewMode(mode);
  }, [setViewMode]);

  const setActiveProjectTabWithTransition = useCallback((tab: 'all' | 'active' | 'archived') => {
    setActiveProjectTab(tab);
  }, [setActiveProjectTab]);

  const setActiveMetricsTabWithTransition = useCallback((tab: 'overview' | 'roadmap' | 'time' | 'projects' | 'library' | 'ideaPool' | 'annualReport' | 'activity') => {
    setActiveMetricsTab(tab);
  }, []);

  const clearAllFilters = useCallback(() => {
    setFilterAssignee('all');
    setFilterType('all');
    setFilterStatus('all');
    setFilterPriority('all');
    setSearchQuery('');
  }, [setFilterAssignee, setFilterType, setFilterStatus, setFilterPriority, setSearchQuery]);

  const handleClearAssigneeFilter = useCallback(() => {
    setFilterAssignee('all');
  }, [setFilterAssignee]);

  const handleFilterTasksFromMetrics = useCallback((priority: 'high' | 'medium' | 'low' | 'none', projectId: string) => {
    if (projectId === 'all') {
      setViewMode('globalKanban');
      setFilterPriority(priority);
    } else {
      setCurrentProjectId(projectId);
      const lastView = localStorage.getItem(`gwantt_last_view_${projectId}`);
      const targetView = (lastView && PERSISTENT_PROJECT_VIEWS.includes(lastView)) ? (lastView as ViewMode) : 'kanban';
      setViewMode(targetView);
      setFilterPriority(priority);
    }
  }, [setViewMode, setCurrentProjectId, setFilterPriority]);

  const handleToggleUiMode = useCallback(() => {
    setUiMode(uiMode === 'standard' ? 'expert' : 'standard');
  }, [uiMode, setUiMode]);

  const handleShowBiography = useCallback(() => {
    setShowBiographyModal(true);
  }, [setShowBiographyModal]);

  const handleShowTimeMachine = useCallback(() => {
    setIsTimeMachineOpen(true);
  }, []);

  const handlePrefetchView = useCallback((view: string) => {
    switch (view) {
      case 'metricsDashboard': import('../components/MetricsDashboardView'); break;
      case 'globalCalendar': import('../components/GlobalCalendarView'); break;
      case 'conceptBuilder': import('../components/ConceptBuilder'); break;
      case 'report': import('../components/ReportView'); break;
      case 'communication': import('../components/CommunicationView'); break;
      case 'dashboard': import('../components/CalendarView'); break;
      case 'calendar': import('../components/CalendarView'); break;
      case 'timeline': import('../components/TimelineView'); break;
      case 'mindmap': import('../components/MindMapView'); break;
    }
  }, []);

  return {
    selectedMetricsProjectId,
    setSelectedMetricsProjectId,
    activeAnalysisTab,
    setActiveAnalysisTab,
    selectedAnalysisProjectId,
    setSelectedAnalysisProjectId,
    activeMetricsTab,
    setActiveMetricsTab,
    activeCalendarTab,
    setActiveCalendarTab,
    calendarShowAllTasks,
    setCalendarShowAllTasks,
    aiAssistantTab,
    setAiAssistantTab,
    activePoolTab,
    setActivePoolTab,
    hiddenProjectIds,
    setHiddenProjectIds,
    isTimeMachineOpen,
    setIsTimeMachineOpen,
    calendarShowOtherWorkspace,
    setCalendarShowOtherWorkspace,
    isRightSidebarOpen,
    setIsRightSidebarOpen,
    toggleRightSidebar,
    syncProjectViews,
    setSyncProjectViews,
    setActivePoolTabWithTransition,
    setViewModeWithTransition,
    setActiveProjectTabWithTransition,
    setActiveMetricsTabWithTransition,
    clearAllFilters,
    handleClearAssigneeFilter,
    handleFilterTasksFromMetrics,
    handleToggleUiMode,
    handleShowBiography,
    handleShowTimeMachine,
    handlePrefetchView,
  };
}
